import { useState } from 'react';
import { Box } from '@mui/material';
import {
  Breadcrumb,
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  FormSearchResultAdvanced,
  useDataProvider,
  useLocalization,
  useStore,
} from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';

import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  getDefaultValueByControl,
} from '@product-framework/index';

function stripEmptyAndId(data) {
  const { id, ...other } = data;
  return Object.fromEntries(
    Object.entries({ ...other }).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );
}

/**
 * Shared advanced list page with search, breadcrumb, and CRUD list.
 * Use for list pages that share the same layout (Flow Definitions, Managed Numbers, etc.).
 * @param {object} config
 * @param {string} config.storeName - Key for store.domain.list.get/set/reset() (e.g. 'FlowDefinitionList').
 *   The list store instance (e.g. flowDefinitionsListStore) is registered separately on rootStore.uiState.
 * @param {string} config.apiPathSuffix - API path after base URL (e.g. '/sxcl/v1/insights/flow/definitions')
 * @param {{ id: string, defaultMessage: string, icon: ReactNode }} config.breadcrumb
 * @param {{ id: string, defaultMessage: string }} config.searchHeading
 * @param {{ id: string, defaultMessage: string }} config.listHeading
 * @param {object} config.accessorFunctions - Column accessors for the list
 * @param {object} config.listSchema
 * @param {object} config.searchSchema
 * @param {function} config.validationSchema - (formatMessage) => yup schema
 * @param {function} [config.valueFormatter] - Optional; default ({ value }) => value
 * @param {Array} [config.extraStackableButtons] - Optional; buttons shown before Show/Hide Search (e.g. Create). Each: { label, permissionName?, Icon, variant?, onClick }
 */
function AdvancedListPage({
  storeName,
  apiPathSuffix,
  breadcrumb,
  searchHeading,
  listHeading,
  accessorFunctions,
  listSchema,
  searchSchema,
  validationSchema: validationSchemaFn,
  valueFormatter = ({ value }) => value,
  extraStackableButtons,
}) {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [resultsFound, setResultsFound] = useState(null);
  const store = useStore();
  const dataProvider = useDataProvider('default');

  const listApiUrl = `${dataProvider.getApiUrl()}${apiPathSuffix}`;

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => navigate('/dashboard'),
    },
    {
      name: formatMessage({
        id: breadcrumb.id,
        defaultMessage: breadcrumb.defaultMessage,
      }),
      icon: breadcrumb.icon,
      onClick: undefined,
    },
  ];

  const searchToggleButton = {
    label: !showSearch
      ? formatMessage({
          id: 'SHOW_SEARCH_STACKABLE_BUTTON',
          defaultMessage: 'Show Search',
        })
      : formatMessage({
          id: 'HIDE_SEARCH_STACKABLE_BUTTON',
          defaultMessage: 'Hide Search',
        }),
    Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
    variant: 'contained',
    onClick: () => setShowSearch(!showSearch),
  };
  const stackablePageButtons = [
    ...(Array.isArray(extraStackableButtons) ? extraStackableButtons : []),
    searchToggleButton,
  ];

  const submitSearchForm = async data => {
    const requestData = stripEmptyAndId(data);
    const searchState = createSearchState(requestData, searchSchema);
    store.domain.list.set(storeName, searchState);
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName,
    dataProvider,
    schema: listSchema,
    initialState: { showColumnFilters: false },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions,
    onRowCountChange: rowCount => setResultsFound(rowCount),
  };

  const searchProps = {
    customComponents: FormFields,
    storeName,
    IconLibrary,
    schema: searchSchema,
    onSubmit: submitSearchForm,
    onClear: () => store.domain.list.reset(storeName),
    validationSchema: validationSchemaFn(formatMessage),
    hideSearchForm: () => setShowSearch(false),
    getValueByControl: getDefaultValueByControl,
  };

  const searchParamsProps = {
    preProcessSearchValues: x => x,
    storeName,
    schema: searchSchema,
    resultsFound,
    valueFormatter,
  };

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <Grid2 xs={12}>
        <FormSearchResultAdvanced {...searchParamsProps} />
      </Grid2>

      {showSearch && (
        <PageSection
          containerProps={{ sx: { mb: 3 } }}
          label={formatMessage({
            id: searchHeading.id,
            defaultMessage: searchHeading.defaultMessage,
          })}>
          <Box>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
              searchProps={searchProps}
            />
          </Box>
        </PageSection>
      )}

      <PageSection
        label={formatMessage({
          id: listHeading.id,
          defaultMessage: listHeading.defaultMessage,
        })}>
        <Box>
          <CRUDComponent
            type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
            listProps={listProps}
          />
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(AdvancedListPage);
